(() => {
  const MOBILE_BREAKPOINT = "(max-width: 767px)";
  const ZOOM_SCALE = 2;
  const MOVE_THRESHOLD = 8;
  const TAP_MAX_DURATION = 350;
  const stateMap = new WeakMap(); // frame -> state
  const gestureMap = new WeakMap(); // frame -> touch gesture state
  const mobileQuery = window.matchMedia(MOBILE_BREAKPOINT);

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const getState = (frame, image, slide) => {
    let state = stateMap.get(frame);
    if (!state) {
      state = {
        zoomed: false,
        offsetX: 0,
        offsetY: 0,
        panStartX: 0,
        panStartY: 0,
      };
      stateMap.set(frame, state);
    }
    state.image = image;
    state.slide = slide;
    state.frame = frame;
    return state;
  };

  const getBounds = (state) => {
    const width = state.image.clientWidth;
    const height = state.image.clientHeight;
    const maxX = ((width * ZOOM_SCALE) - width) / 2;
    const maxY = ((height * ZOOM_SCALE) - height) / 2;
    return { maxX, maxY };
  };

  const applyTransform = (state) => {
    const scale = state.zoomed ? ZOOM_SCALE : 1;
    state.image.style.transform = `translate3d(${state.offsetX}px, ${state.offsetY}px, 0) scale(${scale})`;
    state.slide.classList.toggle("mobile-zoom-active", state.zoomed);
    state.frame.style.touchAction = state.zoomed ? "none" : "manipulation";
  };

  const resetZoom = (state) => {
    state.zoomed = false;
    state.offsetX = 0;
    state.offsetY = 0;
    applyTransform(state);
  };

  const zoomInAtPoint = (state, clientX, clientY) => {
    const rect = state.image.getBoundingClientRect();
    const pointX = clientX - rect.left;
    const pointY = clientY - rect.top;
    const { maxX, maxY } = getBounds(state);

    state.zoomed = true;
    state.offsetX = clamp((rect.width / 2 - pointX) * (ZOOM_SCALE - 1), -maxX, maxX);
    state.offsetY = clamp((rect.height / 2 - pointY) * (ZOOM_SCALE - 1), -maxY, maxY);
    applyTransform(state);
  };

  const getVisibleImage = (container) => {
    const mobileImage = container.querySelector("img.media.media--mobile");
    if (mobileImage && mobileImage.offsetParent !== null) {
      return mobileImage;
    }

    const images = container.querySelectorAll("img.media");
    for (let index = 0; index < images.length; index += 1) {
      if (images[index].offsetParent !== null) {
        return images[index];
      }
    }

    return container.querySelector("img.media");
  };

  const getTouchTarget = (eventTarget, sectionRoot) => {
    const galleryAnchor = eventTarget.closest(".product-page__gallery-slide--image > a");
    if (galleryAnchor && sectionRoot.contains(galleryAnchor)) {
      const slide = galleryAnchor.closest(".product-page__gallery-slide--image");
      const image = getVisibleImage(galleryAnchor);
      if (slide && image) {
        return {
          slide,
          image,
          frame: galleryAnchor,
          isAnchorMode: true,
        };
      }
    }

    const heroSlide = eventTarget.closest(".custom-product-hero__slide--image");
    if (heroSlide && sectionRoot.contains(heroSlide)) {
      const frame = heroSlide.querySelector(".custom-product-hero__media-card");
      const image = getVisibleImage(heroSlide);
      if (frame && image) {
        return {
          slide: heroSlide,
          image,
          frame,
          isAnchorMode: false,
        };
      }
    }

    return null;
  };

  const primeZoomStyles = (frame, image) => {
    if (frame.dataset.mobileZoomStyled !== "true") {
      frame.style.overflow = "hidden";
      frame.style.touchAction = "manipulation";
      frame.dataset.mobileZoomStyled = "true";
    }

    if (image.dataset.mobileZoomStyled !== "true") {
      image.style.transform = "translate3d(0, 0, 0) scale(1)";
      image.style.transformOrigin = "center center";
      image.style.transition = "transform 0.25s ease";
      image.style.willChange = "transform";
      image.dataset.mobileZoomStyled = "true";
    }
  };

  const initSection = (sectionRoot) => {
    if (!sectionRoot || sectionRoot.dataset.mobileZoomBound === "true") {
      return;
    }

    sectionRoot.dataset.mobileZoomBound = "true";

    sectionRoot.addEventListener(
      "touchstart",
      (event) => {
        if (!mobileQuery.matches || event.touches.length !== 1) {
          return;
        }

        const target = getTouchTarget(event.target, sectionRoot);
        if (!target) {
          return;
        }

        primeZoomStyles(target.frame, target.image);
        const state = getState(target.frame, target.image, target.slide);
        const touch = event.touches[0];

        gestureMap.set(target.frame, {
          startX: touch.clientX,
          startY: touch.clientY,
          moved: false,
          startedAt: Date.now(),
        });

        if (state.zoomed) {
          state.panStartX = state.offsetX;
          state.panStartY = state.offsetY;
        }
      },
      { passive: true, capture: true }
    );

    sectionRoot.addEventListener(
      "touchmove",
      (event) => {
        if (!mobileQuery.matches || event.touches.length !== 1) {
          return;
        }

        const target = getTouchTarget(event.target, sectionRoot);
        if (!target) {
          return;
        }

        primeZoomStyles(target.frame, target.image);
        const state = getState(target.frame, target.image, target.slide);
        const gesture = gestureMap.get(target.frame);
        if (!gesture) {
          return;
        }

        const touch = event.touches[0];
        const deltaX = touch.clientX - gesture.startX;
        const deltaY = touch.clientY - gesture.startY;

        if (Math.abs(deltaX) > MOVE_THRESHOLD || Math.abs(deltaY) > MOVE_THRESHOLD) {
          gesture.moved = true;
        }

        if (!state.zoomed) {
          return;
        }

        const { maxX, maxY } = getBounds(state);
        state.offsetX = clamp(state.panStartX + deltaX, -maxX, maxX);
        state.offsetY = clamp(state.panStartY + deltaY, -maxY, maxY);
        target.image.style.transition = "transform 0.08s linear";
        applyTransform(state);

        event.preventDefault();
        event.stopPropagation();
      },
      { passive: false, capture: true }
    );

    sectionRoot.addEventListener(
      "touchend",
      (event) => {
        if (!mobileQuery.matches || event.changedTouches.length === 0) {
          return;
        }

        const target = getTouchTarget(event.target, sectionRoot);
        if (!target) {
          return;
        }

        primeZoomStyles(target.frame, target.image);
        const state = getState(target.frame, target.image, target.slide);
        const gesture = gestureMap.get(target.frame);
        if (!gesture) {
          return;
        }

        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - gesture.startX;
        const deltaY = touch.clientY - gesture.startY;
        const moved = gesture.moved || Math.abs(deltaX) > MOVE_THRESHOLD || Math.abs(deltaY) > MOVE_THRESHOLD;
        const isTap = !moved && Date.now() - gesture.startedAt <= TAP_MAX_DURATION;

        gestureMap.delete(target.frame);

        if (!isTap) {
          return;
        }

        target.image.style.transition = "transform 0.25s ease";

        event.preventDefault();
        event.stopPropagation();

        if (state.zoomed) {
          resetZoom(state);
          return;
        }

        zoomInAtPoint(state, touch.clientX, touch.clientY);
      },
      { passive: false, capture: true }
    );
  };

  const resetAllZooms = () => {
    document.querySelectorAll(".mobile-zoom-active").forEach((slide) => {
      const frame = slide.querySelector(":scope > a") || slide.querySelector(".custom-product-hero__media-card");
      if (!frame) return;
      const state = stateMap.get(frame);
      if (!state) return;
      resetZoom(state);
    });
  };

  const initAll = () => {
    document.querySelectorAll(".product-page__section").forEach((sectionRoot) => initSection(sectionRoot));
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => {
    if (event.target && event.target.classList.contains("product-page__section")) {
      initSection(event.target);
    }
  });

  mobileQuery.addEventListener("change", () => {
    if (!mobileQuery.matches) {
      resetAllZooms();
    }
  });
})();
